// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { isIOS } from '@fluentui/react';
import { mergeStyles, Stack } from '@fluentui/react';
import { PersonaSize } from '@fluentui/react';
import { CommunicationParticipant, ErrorBar, MessageProps, MessageRenderer, MessageThread, MessageThreadStyles, ParticipantMenuItemsCallback, SendBoxStylesProps, TypingIndicator, TypingIndicatorStylesProps, useTheme } from '@internal/react-components';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { AvatarPersona, AvatarPersonaDataCallback, AvatarPersonaProps } from '../common/AvatarPersona';
import { useAdapter } from './adapter/ChatAdapterProvider';
import { ChatCompositeOptions } from './ChatComposite';
import { ChatHeader, getHeaderProps } from './ChatHeader';
import { useAdaptedSelector } from './hooks/useAdaptedSelector';
import { usePropsFor } from './hooks/usePropsFor';
import { chatArea, chatContainer, chatWrapper, messageThreadChatCompositeStyles, sendboxContainerStyles, typingIndicatorContainerStyles } from './styles/Chat.styles';
import { participantListContainerPadding } from '../common/styles/ParticipantContainer.styles';
import { toFlatCommunicationIdentifier } from '@internal/acs-ui-common';
import { _AttachmentDownloadCards } from '@internal/react-components';
import { ImageOverlay } from '@internal/react-components';
import { InlineImage } from '@internal/react-components';
import { ChatMessageWithStatus, ResourceFetchResult } from '@internal/chat-stateful-client';
import { SendBoxPicker } from '../common/SendBoxPicker';
import { useSelector } from './hooks/useSelector';
import { getChatMessages, getThreadId, getUserId } from './selectors/baseSelectors';
/**
 * @private
 */
export type ChatScreenProps = {
  options?: ChatCompositeOptions;
  onFetchAvatarPersonaData?: AvatarPersonaDataCallback;
  onRenderMessage?: (messageProps: MessageProps, defaultOnRender?: MessageRenderer) => JSX.Element;
  onRenderTypingIndicator?: (typingUsers: CommunicationParticipant[]) => JSX.Element;
  onFetchParticipantMenuItems?: ParticipantMenuItemsCallback;
  styles?: ChatScreenStyles;
  formFactor?: 'desktop' | 'mobile';
};

/**
 * @private
 */
export type ChatScreenStyles = {
  messageThread?: MessageThreadStyles;
  sendBox?: SendBoxStylesProps;
  typingIndicator?: TypingIndicatorStylesProps;
};

/**
 * @private
 */
interface OverlayImageItem {
  imageSrc: string;
  title: string;
  titleIcon: JSX.Element;
  attachmentId: string;
  messageId: string;
  imageUrl: string;
}

/**
 * @private
 */
export const ChatScreen = (props: ChatScreenProps): JSX.Element => {
  const {
    onFetchAvatarPersonaData,
    onRenderMessage,
    onRenderTypingIndicator,
    options,
    styles,
    formFactor
  } = props;
  const defaultNumberOfChatMessagesToReload = 5;
  const [overlayImageItem, setOverlayImageItem] = useState<OverlayImageItem>();
  const [isImageOverlayOpen, setIsImageOverlayOpen] = useState<boolean>(false);
  const adapter = useAdapter();
  const theme = useTheme();
  useEffect(() => {
    // Initial data should be always fetched by the composite(or external caller) instead of the adapter
    const fetchData: () => Promise<void> = async () => {
      // Fetch initial data for adapter
      await adapter.fetchInitialData();
      // Fetch initial set of messages. Without fetching messages here, if the Composite's adapter is changed the message thread does not load new messages.
      await adapter.loadPreviousChatMessages(defaultNumberOfChatMessagesToReload);
    };
    fetchData();
  }, [adapter]);
  const messageThreadProps = usePropsFor(MessageThread);
  const typingIndicatorProps = usePropsFor(TypingIndicator);
  const headerProps = useAdaptedSelector(getHeaderProps);
  const errorBarProps = usePropsFor(ErrorBar);
  const overlayImageItemRef = React.useRef<OverlayImageItem | undefined>(overlayImageItem);
  overlayImageItemRef.current = overlayImageItem;
  const adapterChatMessages = useSelector(getChatMessages);
  useEffect(() => {
    if (overlayImageItemRef.current === undefined) {
      return;
    }
    const message: ChatMessageWithStatus | undefined = adapterChatMessages[overlayImageItemRef.current.messageId];
    if (message === undefined) {
      return;
    }
    const resourceFetchResult = message.resourceCache?.[overlayImageItemRef.current.imageUrl];
    if (overlayImageItemRef.current.imageSrc === '' && resourceFetchResult) {
      const fullSizeImageSrc = getResourceSourceUrl(resourceFetchResult);
      if (fullSizeImageSrc === undefined || fullSizeImageSrc === '' || overlayImageItemRef.current.imageSrc === fullSizeImageSrc) {
        return;
      }
      setOverlayImageItem({
        ...overlayImageItemRef.current,
        imageSrc: fullSizeImageSrc
      });
    }
  }, [adapterChatMessages]);
  const getResourceSourceUrl = (result: ResourceFetchResult): string => {
    let src = '';
    if (result.error || !result.sourceUrl) {
      src = 'blob://';
    } else {
      src = result.sourceUrl;
    }
    return src;
  };
  const onRenderAvatarCallback = useCallback((userId?: string, defaultOptions?: AvatarPersonaProps) => {
    return <AvatarPersona userId={userId} hidePersonaDetails={true} {...defaultOptions} dataProvider={onFetchAvatarPersonaData} />;
  }, [onFetchAvatarPersonaData]);
  const messageThreadStyles = useMemo(() => {
    return Object.assign({}, messageThreadChatCompositeStyles(theme.semanticColors.bodyBackground), styles?.messageThread);
  }, [styles?.messageThread, theme.semanticColors.bodyBackground]);
  const typingIndicatorStyles = useMemo(() => {
    return Object.assign({}, styles?.typingIndicator);
  }, [styles?.typingIndicator]);
  const sendBoxStyles = useMemo(() => {
    return Object.assign({}, styles?.sendBox);
  }, [styles?.sendBox]);
  const userIdObject = useSelector(getUserId);
  const userId = toFlatCommunicationIdentifier(userIdObject);
  const threadId = useSelector(getThreadId);
  const onInlineImageClicked = useCallback((attachmentId: string, messageId: string) => {
    const message: ChatMessageWithStatus | undefined = adapterChatMessages[messageId];
    const inlinedImages = message?.content?.attachments?.filter(attachment => {
      return attachment.attachmentType === 'image' && attachment.id === attachmentId;
    });
    const attachment = inlinedImages?.[0];
    if (!attachment) {
      return;
    }
    let imageSrc = '';
    if (attachment.url) {
      const resourceFetchResult = message?.resourceCache?.[attachment.url];
      if (resourceFetchResult) {
        imageSrc = getResourceSourceUrl(resourceFetchResult);
      } else {
        adapter.downloadResourceToCache({
          threadId,
          messageId,
          resourceUrl: attachment.url
        });
      }
    }
    const titleIconRenderOptions = {
      text: message?.senderDisplayName,
      size: PersonaSize.size32,
      showOverflowTooltip: false,
      imageAlt: message?.senderDisplayName
    };
    const messageSenderId = message?.sender !== undefined ? toFlatCommunicationIdentifier(message.sender) : userId;
    const titleIcon = onRenderAvatarCallback && onRenderAvatarCallback(messageSenderId, titleIconRenderOptions);
    const overlayImage: OverlayImageItem = {
      title: message?.senderDisplayName || '',
      titleIcon: titleIcon,
      attachmentId: attachment.id,
      imageSrc: imageSrc,
      messageId: messageId,
      imageUrl: attachment.url || ''
    };
    setIsImageOverlayOpen(true);
    setOverlayImageItem(overlayImage);
  }, [adapter, adapterChatMessages, onRenderAvatarCallback, userId, threadId]);
  const onRenderInlineImage = useCallback((inlineImage: InlineImage, defaultOnRender: (inlineImage: InlineImage) => JSX.Element): JSX.Element => {
    const message = adapterChatMessages[inlineImage.messageId];
    const attachment = message?.content?.attachments?.find(attachment => attachment.id === inlineImage.imageAttributes.id);
    if (attachment === undefined) {
      return defaultOnRender(inlineImage);
    }
    let pointerEvents: 'none' | 'auto' = inlineImage.imageAttributes.src === '' ? 'none' : 'auto';
    const resourceCache = message?.resourceCache;
    if (resourceCache && attachment.previewUrl && resourceCache[attachment.previewUrl] && resourceCache[attachment.previewUrl]?.error) {
      pointerEvents = 'none';
    }
    return <span key={inlineImage.imageAttributes.id} onClick={() => onInlineImageClicked(inlineImage.imageAttributes.id || '', inlineImage.messageId)} tabIndex={0} role="button" onKeyDown={e => {
      if (e.key === 'Enter') {
        onInlineImageClicked(inlineImage.imageAttributes.id || '', inlineImage.messageId);
      }
    }} style={{
      cursor: 'pointer',
      pointerEvents
    }}>
          {defaultOnRender(inlineImage)}
        </span>;
  }, [adapterChatMessages, onInlineImageClicked]);
  const inlineImageOptions = useMemo(() => {
    return {
      onRenderInlineImage: onRenderInlineImage
    };
  }, [onRenderInlineImage]);
  const onDownloadButtonClicked = useCallback((imageSrc: string): void => {
    if (imageSrc === '') {
      return;
    }
    if (isIOS()) {
      window.open(imageSrc, '_blank');
    } else {
      // Create a new anchor element
      const a = document.createElement('a');
      // Set the href and download attributes for the anchor element
      a.href = imageSrc;
      a.download = overlayImageItem?.attachmentId || '';
      a.rel = 'noopener noreferrer';
      a.target = '_blank';

      // Programmatically click the anchor element to trigger the download
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }, [overlayImageItem?.attachmentId]);
  const onSendMessageHandler = useCallback(async function (content: string) {
    await adapter.sendMessage(content, {});
  }, [adapter]);
  const onUpdateMessageHandler = useCallback(async function (messageId: string, content: string) {
    await messageThreadProps.onUpdateMessage(messageId, content);
  }, [messageThreadProps]);
  return <Stack className={chatContainer} grow>
      {options?.topic !== false && <ChatHeader {...headerProps} />}
      <Stack className={chatArea} tokens={participantListContainerPadding} horizontal grow>
        <Stack className={chatWrapper} grow>
          {options?.errorBar !== false && <ErrorBar {...errorBarProps} />}
          {}
          <MessageThread {...messageThreadProps} onUpdateMessage={onUpdateMessageHandler} onRenderAvatar={onRenderAvatarCallback} onRenderMessage={onRenderMessage} inlineImageOptions={inlineImageOptions} numberOfChatMessagesToReload={defaultNumberOfChatMessagesToReload} styles={messageThreadStyles} />
          <Stack className={mergeStyles(sendboxContainerStyles)}>
            <div className={mergeStyles(typingIndicatorContainerStyles)}>
              {onRenderTypingIndicator ? onRenderTypingIndicator(typingIndicatorProps.typingUsers) : <TypingIndicator {...typingIndicatorProps} styles={typingIndicatorStyles} />}
            </div>
            <Stack horizontal={formFactor === 'mobile'}>
              {formFactor === 'mobile'}
              <Stack grow>
                <SendBoxPicker styles={sendBoxStyles} autoFocus={options?.autoFocus}
              // we need to overwrite onSendMessage for SendBox because we need to clear attachment state
              // when submit button is clicked
              onSendMessage={onSendMessageHandler} />
              </Stack>
              {formFactor !== 'mobile'}
            </Stack>
          </Stack>
        </Stack>
        {}
      </Stack>
      {overlayImageItem && <ImageOverlay {...overlayImageItem} isOpen={isImageOverlayOpen} onDismiss={() => {
      setOverlayImageItem(undefined);
      setIsImageOverlayOpen(false);
      adapter.removeResourceFromCache({
        threadId,
        messageId: overlayImageItem.messageId,
        resourceUrl: overlayImageItem.imageUrl
      });
    }} onDownloadButtonClicked={onDownloadButtonClicked} />}
    </Stack>;
};